import { motion } from 'framer-motion'
import { ArrowRight, Inbox, Command, MousePointerClick } from 'lucide-react'

interface OnboardingStepReadyProps {
  onFinish: () => void
  hasProject: boolean
}

const hints = [
  {
    icon: Inbox,
    title: 'Explore your inbox',
    description: 'Your welcome messages explain each feature. Press E to complete them as you go.',
  },
  {
    icon: Command,
    title: 'Try the command palette',
    description: 'Press Cmd+K to jump anywhere.',
  },
  {
    icon: MousePointerClick,
    title: 'Launch the widget',
    description: 'Press W to start a feedback session on your site.',
  },
]

export function OnboardingStepReady({ onFinish, hasProject }: OnboardingStepReadyProps) {
  return (
    <motion.div
      key="step-ready"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-lg mx-auto text-center"
    >
      {/* Confetti-like dots */}
      <div className="relative h-16 mb-4">
        {Array.from({ length: 12 }, (_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0.6], scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 + i * 0.05 }}
            className="absolute w-2 h-2 rounded-full"
            style={{
              left: `${20 + (i * 5.5)}%`,
              top: `${20 + Math.sin(i * 1.2) * 30}%`,
              backgroundColor: [
                '#FFD500', '#a7bbec', '#059669', '#FFD500', '#a7bbec', '#059669',
                '#FFD500', '#a7bbec', '#059669', '#FFD500', '#a7bbec', '#059669',
              ][i],
            }}
          />
        ))}
      </div>

      <h2 className="text-2xl font-bold text-framed-neutral-800 tracking-tight font-display mb-2">
        You're all set
      </h2>
      <p className="text-sm text-framed-neutral-500 mb-8">
        {hasProject
          ? 'Your project is ready. Here are a few things to try first.'
          : 'You can create your first project from the dashboard.'}
      </p>

      {hasProject && (
        <div className="space-y-3 mb-8 text-left">
          {hints.map((hint, index) => (
            <motion.div
              key={hint.title}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
              className="flex items-start gap-3 bg-white rounded-xl border border-framed-neutral-300 p-4"
            >
              <div className="w-8 h-8 rounded-lg bg-framed-neutral-100 flex items-center justify-center flex-shrink-0">
                <hint.icon className="w-4 h-4 text-framed-neutral-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-framed-neutral-800 mb-0.5">
                  {hint.title}
                </h3>
                <p className="text-xs text-framed-neutral-500">
                  {hint.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <button
        onClick={onFinish}
        className="inline-flex items-center gap-2 px-6 py-2.5 bg-framed-neutral-800 text-white rounded-lg font-medium hover:bg-framed-neutral-900 hover:scale-[1.02] transition-all"
      >
        {hasProject ? 'Go to your project' : 'Go to dashboard'}
        <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  )
}
